import { Ether, Token } from '@uniswap/sdk-core'
import { nearestUsableTick } from '@uniswap/v3-sdk'
import { Pool } from '@uniswap/v4-sdk'
import type { PublicClient } from 'viem'

export const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000' as const
export const ZERO_BYTES32 = '0x0000000000000000000000000000000000000000000000000000000000000000' as const

export function getStateViewAddress(_chainId: number): `0x${string}` {
  return '0xA3c0c9b65baD0b08107Aa264b0f3dB444b867A71'
}

export function getV4PositionManagerAddress(_chainId: number): `0x${string}` {
  return '0x7C5f5A4bBd8fD63184577525326123B519429bDc'
}

export const DELI_HOOK_CP_ADDRESS = '0x95AFBC0FCcF974B41380f24e562f15b6DD90faC8' as const
export const POSITION_MANAGER_ADAPTER_ADDRESS = '0xFD9Ca05205c01b71D2429192827F906661763656' as const
export const DAILY_EPOCH_GAUGE_ADDRESS = '0xd8Df4f5a4aE2db7EA724d1Ee451806167F01F0DC' as const
export const INCENTIVE_GAUGE_ADDRESS = '0x59d7bFF1934939A7cD11E029c051Ba9872469213' as const

export function getV4PoolManagerAddress(_chainId: number): `0x${string}` {
  return '0x498581fF718922c3f8e6A244956aF099B2652b2b'
}

// Permit2 (shared across EVM chains)
export const PERMIT2_ADDRESS = '0x000000000022D473030F116dDEE9F6B43aC78BA3' as const

export const PERMIT2_ABI = [
  {
    type: 'function',
    name: 'allowance',
    stateMutability: 'view',
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'token', type: 'address' },
      { name: 'spender', type: 'address' },
    ],
    outputs: [
      { name: 'amount', type: 'uint160' },
      { name: 'expiration', type: 'uint48' },
      { name: 'nonce', type: 'uint48' },
    ],
  },
  {
    type: 'function',
    name: 'approve',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'token', type: 'address' },
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint160' },
      { name: 'expiration', type: 'uint48' },
    ],
    outputs: [],
  },
] as const

export const STATE_VIEW_ABI = [
  {
    type: 'function',
    name: 'getSlot0',
    stateMutability: 'view',
    inputs: [{ name: 'poolId', type: 'bytes32' }],
    outputs: [
      { name: 'sqrtPriceX96', type: 'uint160' },
      { name: 'tick', type: 'int24' },
      { name: 'protocolSwapFee', type: 'uint24' },
    ],
  },
  {
    type: 'function',
    name: 'getLiquidity',
    stateMutability: 'view',
    inputs: [{ name: 'poolId', type: 'bytes32' }],
    outputs: [{ name: 'liquidity', type: 'uint128' }],
  },
] as const

export const V4_POOL_MANAGER_ABI = [
  {
    type: 'function',
    name: 'initialize',
    stateMutability: 'nonpayable',
    inputs: [
      {
        name: 'key',
        type: 'tuple',
        components: [
          { name: 'currency0', type: 'address' },
          { name: 'currency1', type: 'address' },
          { name: 'fee', type: 'uint24' },
          { name: 'tickSpacing', type: 'int24' },
          { name: 'hooks', type: 'address' },
        ],
      },
      { name: 'sqrtPriceX96', type: 'uint160' },
    ],
    outputs: [],
  },
] as const

export const V4_POSITION_MANAGER_ABI = [
  {
    inputs: [
      { internalType: 'uint256', name: 'tokenId', type: 'uint256' },
      { internalType: 'address', name: 'newSubscriber', type: 'address' },
      { internalType: 'bytes', name: 'data', type: 'bytes' },
    ],
    name: 'subscribe',
    outputs: [],
    stateMutability: 'payable',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'uint256', name: 'tokenId', type: 'uint256' }],
    name: 'subscriber',
    outputs: [{ internalType: 'contract ISubscriber', name: 'subscriber', type: 'address' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'address', name: 'from', type: 'address' },
      { internalType: 'address', name: 'to', type: 'address' },
      { internalType: 'uint256', name: 'tokenId', type: 'uint256' },
    ],
    name: 'safeTransferFrom',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
] as const

export const DELI_HOOK_CP_ABI = [
  {
    inputs: [
      {
        components: [
          { internalType: 'Currency', name: 'currency0', type: 'address' },
          { internalType: 'Currency', name: 'currency1', type: 'address' },
          { internalType: 'uint24', name: 'fee', type: 'uint24' },
          { internalType: 'int24', name: 'tickSpacing', type: 'int24' },
          { internalType: 'contract IHooks', name: 'hooks', type: 'address' },
        ],
        internalType: 'struct PoolKey',
        name: 'key',
        type: 'tuple',
      },
      {
        components: [
          { internalType: 'uint256', name: 'amount0Desired', type: 'uint256' },
          { internalType: 'uint256', name: 'amount1Desired', type: 'uint256' },
          { internalType: 'uint256', name: 'amount0Min', type: 'uint256' },
          { internalType: 'uint256', name: 'amount1Min', type: 'uint256' },
          { internalType: 'uint256', name: 'deadline', type: 'uint256' },
          { internalType: 'int24', name: 'tickLower', type: 'int24' },
          { internalType: 'int24', name: 'tickUpper', type: 'int24' },
          { internalType: 'bytes32', name: 'userInputSalt', type: 'bytes32' },
        ],
        internalType: 'struct MultiPoolCustomCurve.AddLiquidityParams',
        name: 'params',
        type: 'tuple',
      },
    ],
    name: 'addLiquidity',
    outputs: [{ internalType: 'BalanceDelta', name: 'delta', type: 'int256' }],
    stateMutability: 'payable',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'PoolId', name: 'poolId', type: 'bytes32' },
      { internalType: 'address', name: 'account', type: 'address' },
    ],
    name: 'balanceOf',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'uint256', name: 'amountOut', type: 'uint256' },
      { internalType: 'uint256', name: 'reserveIn', type: 'uint256' },
      { internalType: 'uint256', name: 'reserveOut', type: 'uint256' },
      { internalType: 'uint24', name: 'feePips', type: 'uint24' },
    ],
    name: 'getAmountIn',
    outputs: [{ internalType: 'uint256', name: 'amountIn', type: 'uint256' }],
    stateMutability: 'pure',
    type: 'function',
  },
  {
    inputs: [
      {
        components: [
          { internalType: 'Currency', name: 'currency0', type: 'address' },
          { internalType: 'Currency', name: 'currency1', type: 'address' },
          { internalType: 'uint24', name: 'fee', type: 'uint24' },
          { internalType: 'int24', name: 'tickSpacing', type: 'int24' },
          { internalType: 'contract IHooks', name: 'hooks', type: 'address' },
        ],
        internalType: 'struct PoolKey',
        name: 'key',
        type: 'tuple',
      },
      { internalType: 'bool', name: 'zeroForOne', type: 'bool' },
      { internalType: 'uint256', name: 'amountOut', type: 'uint256' },
    ],
    name: 'getAmountIn',
    outputs: [{ internalType: 'uint256', name: 'amountIn', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'uint256', name: 'amountIn', type: 'uint256' },
      { internalType: 'uint256', name: 'reserveIn', type: 'uint256' },
      { internalType: 'uint256', name: 'reserveOut', type: 'uint256' },
      { internalType: 'uint24', name: 'feePips', type: 'uint24' },
    ],
    name: 'getAmountOut',
    outputs: [{ internalType: 'uint256', name: 'amountOut', type: 'uint256' }],
    stateMutability: 'pure',
    type: 'function',
  },
  {
    inputs: [
      {
        components: [
          { internalType: 'Currency', name: 'currency0', type: 'address' },
          { internalType: 'Currency', name: 'currency1', type: 'address' },
          { internalType: 'uint24', name: 'fee', type: 'uint24' },
          { internalType: 'int24', name: 'tickSpacing', type: 'int24' },
          { internalType: 'contract IHooks', name: 'hooks', type: 'address' },
        ],
        internalType: 'struct PoolKey',
        name: 'key',
        type: 'tuple',
      },
      { internalType: 'bool', name: 'zeroForOne', type: 'bool' },
      { internalType: 'uint256', name: 'amountIn', type: 'uint256' },
    ],
    name: 'getAmountOut',
    outputs: [{ internalType: 'uint256', name: 'amountOut', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'uint256', name: 'amountOut', type: 'uint256' },
      {
        components: [
          { internalType: 'Currency', name: 'currency0', type: 'address' },
          { internalType: 'Currency', name: 'currency1', type: 'address' },
          { internalType: 'uint24', name: 'fee', type: 'uint24' },
          { internalType: 'int24', name: 'tickSpacing', type: 'int24' },
          { internalType: 'contract IHooks', name: 'hooks', type: 'address' },
        ],
        internalType: 'struct PoolKey[]',
        name: 'route',
        type: 'tuple[]',
      },
      { internalType: 'bool[]', name: 'zeroForOnes', type: 'bool[]' },
    ],
    name: 'getAmountsIn',
    outputs: [{ internalType: 'uint256[]', name: 'amounts', type: 'uint256[]' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'uint256', name: 'amountIn', type: 'uint256' },
      {
        components: [
          { internalType: 'Currency', name: 'currency0', type: 'address' },
          { internalType: 'Currency', name: 'currency1', type: 'address' },
          { internalType: 'uint24', name: 'fee', type: 'uint24' },
          { internalType: 'int24', name: 'tickSpacing', type: 'int24' },
          { internalType: 'contract IHooks', name: 'hooks', type: 'address' },
        ],
        internalType: 'struct PoolKey[]',
        name: 'route',
        type: 'tuple[]',
      },
      { internalType: 'bool[]', name: 'zeroForOnes', type: 'bool[]' },
    ],
    name: 'getAmountsOut',
    outputs: [{ internalType: 'uint256[]', name: 'amounts', type: 'uint256[]' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'PoolId', name: 'poolId', type: 'bytes32' }],
    name: 'getReserves',
    outputs: [
      { internalType: 'uint128', name: '', type: 'uint128' },
      { internalType: 'uint128', name: '', type: 'uint128' },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'contract IPoolManager', name: '', type: 'address' },
      { internalType: 'PoolId', name: 'poolId', type: 'bytes32' },
    ],
    name: 'getSlot0',
    outputs: [
      { internalType: 'uint160', name: 'sqrtPriceX96', type: 'uint160' },
      { internalType: 'int24', name: 'tick', type: 'int24' },
      { internalType: 'uint24', name: 'protocolFee', type: 'uint24' },
      { internalType: 'uint24', name: 'lpFee', type: 'uint24' },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'PoolId', name: 'poolId', type: 'bytes32' }],
    name: 'getTotalSupply',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'PoolId', name: '', type: 'bytes32' },
      { internalType: 'address', name: '', type: 'address' },
    ],
    name: 'liquidityShares',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'PoolId', name: '', type: 'bytes32' }],
    name: 'poolInitialized',
    outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'PoolId', name: '', type: 'bytes32' }],
    name: 'pools',
    outputs: [
      { internalType: 'uint128', name: 'reserve0', type: 'uint128' },
      { internalType: 'uint128', name: 'reserve1', type: 'uint128' },
      { internalType: 'uint256', name: 'totalSupply', type: 'uint256' },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        components: [
          { internalType: 'Currency', name: 'currency0', type: 'address' },
          { internalType: 'Currency', name: 'currency1', type: 'address' },
          { internalType: 'uint24', name: 'fee', type: 'uint24' },
          { internalType: 'int24', name: 'tickSpacing', type: 'int24' },
          { internalType: 'contract IHooks', name: 'hooks', type: 'address' },
        ],
        internalType: 'struct PoolKey',
        name: 'key',
        type: 'tuple',
      },
      { internalType: 'bool', name: 'zeroForOne', type: 'bool' },
      { internalType: 'uint256', name: 'amountIn', type: 'uint256' },
    ],
    name: 'quote',
    outputs: [{ internalType: 'uint256', name: 'amountOut', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'uint256', name: 'amountA', type: 'uint256' },
      { internalType: 'uint256', name: 'reserveA', type: 'uint256' },
      { internalType: 'uint256', name: 'reserveB', type: 'uint256' },
    ],
    name: 'quote',
    outputs: [{ internalType: 'uint256', name: 'amountB', type: 'uint256' }],
    stateMutability: 'pure',
    type: 'function',
  },
  {
    inputs: [
      {
        components: [
          { internalType: 'Currency', name: 'currency0', type: 'address' },
          { internalType: 'Currency', name: 'currency1', type: 'address' },
          { internalType: 'uint24', name: 'fee', type: 'uint24' },
          { internalType: 'int24', name: 'tickSpacing', type: 'int24' },
          { internalType: 'contract IHooks', name: 'hooks', type: 'address' },
        ],
        internalType: 'struct PoolKey',
        name: 'key',
        type: 'tuple',
      },
      {
        components: [
          { internalType: 'uint256', name: 'liquidity', type: 'uint256' },
          { internalType: 'uint256', name: 'amount0Min', type: 'uint256' },
          { internalType: 'uint256', name: 'amount1Min', type: 'uint256' },
          { internalType: 'uint256', name: 'deadline', type: 'uint256' },
          { internalType: 'int24', name: 'tickLower', type: 'int24' },
          { internalType: 'int24', name: 'tickUpper', type: 'int24' },
          { internalType: 'bytes32', name: 'userInputSalt', type: 'bytes32' },
        ],
        internalType: 'struct MultiPoolCustomCurve.RemoveLiquidityParams',
        name: 'params',
        type: 'tuple',
      },
    ],
    name: 'removeLiquidity',
    outputs: [{ internalType: 'BalanceDelta', name: 'delta', type: 'int256' }],
    stateMutability: 'nonpayable',
    type: 'function',
  },
] as const

export const DAILY_EPOCH_GAUGE_ABI = [
  {
    inputs: [
      { internalType: 'uint256', name: 'tokenId', type: 'uint256' },
      { internalType: 'address', name: 'to', type: 'address' },
    ],
    name: 'claim',
    outputs: [{ internalType: 'uint256', name: 'amount', type: 'uint256' }],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'PoolId[]', name: 'pids', type: 'bytes32[]' },
      { internalType: 'address', name: 'owner', type: 'address' },
    ],
    name: 'claimAllForOwner',
    outputs: [{ internalType: 'uint256', name: 'totalBmx', type: 'uint256' }],
    stateMutability: 'nonpayable',
    type: 'function',
  },
] as const

export const INCENTIVE_GAUGE_ABI = [
  {
    inputs: [
      { internalType: 'uint256', name: 'tokenId', type: 'uint256' },
      { internalType: 'contract IERC20', name: 'token', type: 'address' },
      { internalType: 'address', name: 'to', type: 'address' },
    ],
    name: 'claim',
    outputs: [{ internalType: 'uint256', name: 'amount', type: 'uint256' }],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'PoolId[]', name: 'pids', type: 'bytes32[]' },
      { internalType: 'address', name: 'owner', type: 'address' },
    ],
    name: 'claimAllForOwner',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        components: [
          { internalType: 'Currency', name: 'currency0', type: 'address' },
          { internalType: 'Currency', name: 'currency1', type: 'address' },
          { internalType: 'uint24', name: 'fee', type: 'uint24' },
          { internalType: 'int24', name: 'tickSpacing', type: 'int24' },
          { internalType: 'contract IHooks', name: 'hooks', type: 'address' },
        ],
        internalType: 'struct PoolKey',
        name: 'key',
        type: 'tuple',
      },
      { internalType: 'contract IERC20', name: 'rewardToken', type: 'address' },
      { internalType: 'uint256', name: 'amount', type: 'uint256' },
    ],
    name: 'createIncentive',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
] as const

export function getConstantProductPoolId(
  currency0: Token | typeof Ether.onChain extends (...args: any) => infer R ? R : any,
  currency1: Token | typeof Ether.onChain extends (...args: any) => infer R ? R : any,
  fee: number,
  tickSpacing: number,
  hooks: `0x${string}` = DELI_HOOK_CP_ADDRESS,
): `0x${string}` {
  return Pool.getPoolId(currency0 as any, currency1 as any, fee, tickSpacing, hooks as any) as `0x${string}`
}

export async function fetchDecimals(client: PublicClient, address: `0x${string}`): Promise<number> {
  try {
    const decimals = await client.readContract({
      address,
      abi: [{ name: 'decimals', type: 'function', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint8' }] }],
      functionName: 'decimals',
      args: [],
    })
    return Number(decimals)
  } catch {
    return 18
  }
}

export function alignTicks(
  tickLower: number,
  tickUpper: number,
  tickSpacing: number,
): { tickLower: number; tickUpper: number } {
  const lo = nearestUsableTick(Math.min(tickLower, tickUpper), tickSpacing)
  const hi = nearestUsableTick(Math.max(tickLower, tickUpper), tickSpacing)
  return { tickLower: lo, tickUpper: hi }
}

export async function readPoolState(
  client: PublicClient,
  chainId: number,
  currency0: Token | typeof Ether.onChain extends (...args: any) => infer R ? R : any,
  currency1: Token | typeof Ether.onChain extends (...args: any) => infer R ? R : any,
  fee: number,
  tickSpacing: number,
  hooks?: `0x${string}`,
): Promise<{ sqrtRatioX96: string; currentTick: number; poolLiquidity: string }> {
  const poolId = Pool.getPoolId(currency0 as any, currency1 as any, fee, tickSpacing, (hooks ?? ZERO_ADDRESS) as any)
  const slot0 = await client.readContract({
    address: getStateViewAddress(chainId),
    abi: STATE_VIEW_ABI as any,
    functionName: 'getSlot0',
    args: [poolId as `0x${string}`],
  })
  const liq = await client.readContract({
    address: getStateViewAddress(chainId),
    abi: STATE_VIEW_ABI as any,
    functionName: 'getLiquidity',
    args: [poolId as `0x${string}`],
  })
  const s = slot0 as unknown as readonly [bigint, number, number]
  return { sqrtRatioX96: s[0].toString(), currentTick: s[1], poolLiquidity: (liq as bigint).toString() }
}

export async function readConstantProductReserves(
  client: PublicClient,
  chainId: number,
  currency0: Token | typeof Ether.onChain extends (...args: any) => infer R ? R : any,
  currency1: Token | typeof Ether.onChain extends (...args: any) => infer R ? R : any,
  fee: number,
  tickSpacing: number,
): Promise<{ reserve0: bigint; reserve1: bigint; totalSupply: bigint }> {
  const poolId = getConstantProductPoolId(currency0, currency1, fee, tickSpacing)
  try {
    const [reserve0, reserve1, totalSupply] = (await client.readContract({
      address: DELI_HOOK_CP_ADDRESS,
      abi: DELI_HOOK_CP_ABI as any,
      functionName: 'pools',
      args: [poolId as `0x${string}`],
    })) as readonly [bigint, bigint, bigint]

    return {
      reserve0,
      reserve1,
      totalSupply,
    }
  } catch {
    return { reserve0: BigInt(0), reserve1: BigInt(0), totalSupply: BigInt(0) }
  }
}

export async function readConstantProductUserBalance(
  client: PublicClient,
  chainId: number,
  currency0: Token | typeof Ether.onChain extends (...args: any) => infer R ? R : any,
  currency1: Token | typeof Ether.onChain extends (...args: any) => infer R ? R : any,
  fee: number,
  tickSpacing: number,
  account: `0x${string}`,
): Promise<bigint> {
  try {
    const poolId = getConstantProductPoolId(currency0 as any, currency1 as any, fee, tickSpacing)
    const bal = await (client as any).readContract({
      address: DELI_HOOK_CP_ADDRESS,
      abi: DELI_HOOK_CP_ABI as any,
      functionName: 'balanceOf',
      args: [poolId as `0x${string}`, account],
    })
    return BigInt(bal)
  } catch {
    return BigInt(0)
  }
}

export function buildUseNative(chainId: number, currency0: any, currency1: any) {
  return currency0.isNative ? Ether.onChain(chainId) : currency1.isNative ? Ether.onChain(chainId) : undefined
}

export async function estimateGasFee(
  client: PublicClient,
  account: `0x${string}`,
  to: `0x${string}`,
  data: `0x${string}`,
  value?: bigint,
): Promise<string> {
  const gas = await client.estimateGas({ account, to, data, value })
  const feeData = await client.estimateFeesPerGas()
  const maxFeePerGas = (feeData as any)?.maxFeePerGas ?? (feeData as any)?.gasPrice ?? BigInt('0')
  return (gas * maxFeePerGas).toString()
}
